import React, { useState, useCallback, useEffect } from 'react';
import { Grid, Difficulty, PlayerProfile } from '../../types';
import { GameState as MultiplayerGameState, ChatMessage } from '../../hooks/useMultiplayer';
import { Column } from '../ui';
import { AnimatedNumber, VictoryRain } from '../ui/DiceFx';
import Die from '../Die';
import ChatBox from '../ChatBox';
import DiceBackground from '../DiceBackground';
import { useGridFx } from '../../hooks/useGridFx';
import { calculateColumnScore } from '../../utils';
import { TOAST_DURATION_MS } from '../../constants';
import { useTranslation } from '../../i18n';

const EXP_LEAVE_PENALTY = 40;

interface GameProps {
  playerGrid: Grid;
  opponentGrid: Grid;
  currentDie: number | string | null;
  isDieRolling: boolean;
  gameState: MultiplayerGameState | string | null;
  message: string;
  lastAction: { col: number; target: 'player' | 'opponent' } | null;
  playerTotal: number;
  opponentTotal: number;
  expChange: number | null;
  profile: PlayerProfile;
  difficulty: Difficulty;
  onPlaceDie: (colIdx: number) => void;
  onSelectJokerValue?: (value: number) => void;
  onSelectShieldColumn?: (colIdx: number) => void;
  gameMode?: 'normal' | 'super-dice';
  shields?: { player1: Record<number, number>; player2: Record<number, number> };
  onReset: () => void;
  onLeaveGame?: () => void;
  isMultiplayer?: boolean;
  isConnected?: boolean;
  players?: { id: string; name: string; playerNumber?: number; ready: boolean }[];
  playerNumber?: number;
  onSetReady?: () => void;
  roomId?: string | null;
  countdownNumber?: number | null;
  isMyTurn?: boolean;
  chatMessages?: ChatMessage[];
  onSendChat?: (text: string) => void;
  turnTimer?: number | null;
  turnTimerMax?: number;
  rematchState?: 'none' | 'sent' | 'received' | 'accepted' | 'declined';
  onRequestRematch?: () => void;
  onAcceptRematch?: () => void;
  onDeclineRematch?: () => void;
  isGuest?: boolean;
  onSignUp?: () => void;
}

export const Game: React.FC<GameProps> = ({
  playerGrid,
  opponentGrid,
  currentDie,
  isDieRolling,
  gameState,
  message,
  lastAction,
  playerTotal,
  opponentTotal,
  expChange,
  profile,
  difficulty,
  gameMode = 'normal',
  shields,
  onPlaceDie,
  onSelectJokerValue,
  onSelectShieldColumn,
  onReset,
  onLeaveGame,
  isMultiplayer = false,
  isConnected = false,
  players = [],
  playerNumber = 0,
  onSetReady,
  roomId = null,
  countdownNumber = null,
  isMyTurn = true,
  chatMessages = [],
  onSendChat,
  turnTimer = null,
  turnTimerMax = 3,
  rematchState = 'none',
  onRequestRematch,
  onAcceptRematch,
  onDeclineRematch,
  isGuest = false,
  onSignUp,
}) => {
  const t = useTranslation();
  const boardFx = useGridFx(playerGrid, opponentGrid);
  const [viewport, setViewport] = useState(() => ({
    width: window.innerWidth,
    height: window.innerHeight,
  }));

  useEffect(() => {
    const handleResize = () => {
      setViewport({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const boardScale = (() => {
    if (viewport.width >= 1024) {
      // Desktop: boards + center column need ~1364px; shrink boards on narrower windows
      const BOARD_WIDTH = 490; // column trio incl. hover scale
      const CENTER_SPACE = 430; // center card + game-over stack + gaps + page padding
      return Math.max(0.6, Math.min(1, (viewport.width - CENTER_SPACE) / (2 * BOARD_WIDTH)));
    }

    const { width, height } = viewport;

    // Estimate non-board vertical space (Header + Footer + Center + Gaps)
    // Header: ~60px
    // Center: ~100px
    // Footer: ~30px
    // Gaps/Padding: ~70px
    const NON_BOARD_SPACE = 260;

    // Estimate single board height at scale 1
    const SINGLE_BOARD_HEIGHT = 210;

    // Calculate max available height for boards
    const availableHeightForBoards = height - NON_BOARD_SPACE;

    // We need to fit 2 boards vertically on mobile
    const targetScale = availableHeightForBoards / (2 * SINGLE_BOARD_HEIGHT);

    let scale = Math.min(1, targetScale);

    // Minor adjustment for very narrow screens where width might be the bottleneck
    if (width <= 340) {
      scale = Math.min(scale, 0.85);
    }

    // Allow scaling down significantly for landscape/small screens
    // But keep a sane minimum so it doesn't vanish
    return Math.max(0.45, scale);
  })();

  // Mobile stacks boards vertically (scale toward the center row); desktop
  // sits them left/right of the center card (scale toward it horizontally)
  const isDesktopLayout = viewport.width >= 1024;
  const playerBoardStyle: React.CSSProperties | undefined = boardScale < 1
    ? { transform: `scale(${boardScale})`, transformOrigin: isDesktopLayout ? 'center right' : 'top center' }
    : undefined;
  const opponentBoardStyle: React.CSSProperties | undefined = boardScale < 1
    ? { transform: `scale(${boardScale})`, transformOrigin: isDesktopLayout ? 'center left' : 'bottom center' }
    : undefined;

  // Helper function to check game status
  const getGameStatus = (): string => {
    if (typeof gameState === 'string') return gameState;
    if (gameState?.gameStatus) return gameState.gameStatus;
    return 'waiting';
  };

  const gameStatus = getGameStatus();
  // Single player uses PLAYER_PLACING; multiplayer uses 'placing' + isMyTurn
  const isPlayerPlacing = gameStatus === 'PLAYER_PLACING' || (gameStatus === 'placing' && isMyTurn);
  const isWaiting = gameStatus === 'waiting';
  const isCountdown = gameStatus === 'countdown';
  const isGameOver = gameStatus === 'GAME_OVER' || gameStatus === 'game_over';

  // ── Game-over ceremony ────────────────────────────────────────────────
  const [ceremony, setCeremony] = useState<'win' | 'lose' | 'tie' | null>(null);
  const [showRain, setShowRain] = useState(false);

  useEffect(() => {
    if (!isGameOver) {
      setCeremony(null);
      setShowRain(false);
      return;
    }
    // Totals are final by the time the status flips to game over
    const outcome = playerTotal > opponentTotal ? 'win' : opponentTotal > playerTotal ? 'lose' : 'tie';
    setCeremony(outcome);
    if (outcome === 'win') setShowRain(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGameOver]);
  // Can I roll? Only when it's rolling state AND it's my turn (or single player)
  const canRoll = (gameStatus === 'rolling' && isMyTurn) || gameStatus === 'PLAYER_ROLLING';

  // Am I ready? Is opponent ready?
  const myPlayer = players.find((p) => p.playerNumber === playerNumber);
  const opponentPlayer = players.find((p) => p.playerNumber !== playerNumber);
  const amReady = myPlayer?.ready ?? false;
  const opponentReady = opponentPlayer?.ready ?? false;
  const allPlayersReady = players.length === 2 && players.every((p) => p.ready);
  const opponentJoined = players.length >= 2;

  // ── Leave confirmation popup ──────────────────────────────────────────
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
  const isGameInProgress = isMultiplayer && !['waiting', 'game_over'].includes(gameStatus);

  const handleLeaveClick = () => {
    if (isGameInProgress) {
      setShowLeaveConfirm(true);
    } else {
      onReset();
    }
  };

  // ── Float-up toast ────────────────────────────────────────────────────
  const [toast, setToast] = useState<{ id: number; text: string } | null>(null);

  const showToast = useCallback((text: string) => {
    const id = Date.now();
    setToast({ id, text });
    setTimeout(() => setToast((prev) => (prev?.id === id ? null : prev)), TOAST_DURATION_MS);
  }, []);

  // Copy room URL
  const copyRoomUrl = () => {
    if (roomId) {
      const url = `${window.location.origin}/room/${roomId}`;
      navigator.clipboard.writeText(url);
      showToast(t.copied);
    }
  };

  // Copy room code
  const copyRoomCode = () => {
    if (roomId) {
      navigator.clipboard.writeText(roomId);
      showToast(t.copied);
    }
  };

  return (
    <div className="h-[100dvh] flex flex-col items-center justify-between text-white p-2 lg:p-4 relative select-none bg-[#1a101f] overflow-hidden">
      <DiceBackground count={8} />

      {/* ── Game-over ceremony overlays ─────────────────────────────────── */}
      {showRain && <VictoryRain onDone={() => setShowRain(false)} />}
      {ceremony === 'lose' && (
        <div
          className="defeat-vignette fixed inset-0 z-40 pointer-events-none"
          style={{ background: 'radial-gradient(ellipse at center, transparent 40%, rgba(220, 38, 38, 0.4) 100%)' }}
        />
      )}

      {/* Float-up toast */}
      {toast && (
        <div
          key={toast.id}
          className="fixed left-1/2 bottom-24 z-50 -translate-x-1/2 pointer-events-none"
          style={{ animation: 'toast-float 1.2s ease-out forwards' }}
        >
          <span className="px-4 py-1.5 lg:px-6 lg:py-2 bg-theme text-black text-[9px] lg:text-sm font-bold uppercase tracking-widest pixel-border shadow-lg">
            {toast.text}
          </span>
        </div>
      )}

      {/* ── Leave confirmation popup ────────────────────────────────────── */}
      {showLeaveConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
          <div className="pixel-card bg-[#1a101f] border-2 border-red-500/50 p-6 lg:p-10 flex flex-col items-center gap-4 lg:gap-6 max-w-xs lg:max-w-sm w-full mx-4">
            <h2 className="text-sm lg:text-xl font-bold text-red-400 uppercase tracking-widest text-center">
              {t.leaveGame}
            </h2>
            <p className="text-[8px] lg:text-xs text-gray-400 text-center uppercase leading-relaxed">
              {t.leaveWarning}{' '}
              <span className="text-red-400 font-bold">{t.defeat}</span>.
            </p>
            <div className="text-sm lg:text-lg font-bold text-red-400 animate-pulse">
              -{EXP_LEAVE_PENALTY} {t.exp}
            </div>
            <div className="flex gap-3 w-full">
              <button
                onClick={() => setShowLeaveConfirm(false)}
                className="flex-1 px-3 py-2 lg:px-6 lg:py-3 pixel-border bg-black/60 text-[8px] lg:text-xs text-gray-400 font-bold uppercase hover:bg-black/80 transition-all"
              >
                {t.stay}
              </button>
              <button
                onClick={() => {
                  setShowLeaveConfirm(false);
                  if (onLeaveGame) {
                    onLeaveGame();
                  } else {
                    onReset();
                  }
                }}
                className="flex-1 px-3 py-2 lg:px-6 lg:py-3 pixel-border bg-red-900/60 text-[8px] lg:text-xs text-red-400 font-bold uppercase hover:bg-red-900/80 transition-all"
              >
                {t.leave}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Joker Selection Popup ────────────────────────────────────────── */}
      {isPlayerPlacing && currentDie === 'joker' && onSelectJokerValue && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
          <div className="pixel-card bg-[#1a101f] border-2 border-yellow-500/50 p-6 lg:p-10 flex flex-col items-center gap-4 lg:gap-6 max-w-xs lg:max-w-sm w-full mx-4 relative overflow-hidden">
            <h2 className="text-xl lg:text-3xl font-bold bg-gradient-to-r from-yellow-400 via-pink-400 to-purple-500 text-transparent bg-clip-text uppercase tracking-widest text-center animate-bounce shadow-sm">
              Joker!
            </h2>
            <p className="text-[10px] lg:text-sm text-gray-400 text-center uppercase leading-relaxed font-bold">
              {t.selectJokerValue || 'Select Die Value:'}
            </p>
            <div className="grid grid-cols-3 gap-2 lg:gap-4 w-full">
              {[1, 2, 3, 4, 5, 6].map(v => (
                <button
                  key={v}
                  onClick={() => onSelectJokerValue(v)}
                  className="flex items-center justify-center aspect-square pixel-border bg-gray-900 border-2 border-gray-700 hover:bg-theme/20 hover:border-theme hover:scale-110 transition-all p-1 lg:p-2"
                >
                  <Die value={v} size="md" className="pointer-events-none" />
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── Rematch request popup ────────────────────────────────────── */}
      {rematchState === 'received' && onAcceptRematch && onDeclineRematch && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
          <div className="pixel-card bg-[#1a101f] border-2 border-green-500/50 p-6 lg:p-10 flex flex-col items-center gap-4 lg:gap-6 max-w-xs lg:max-w-sm w-full mx-4">
            <h2 className="text-sm lg:text-xl font-bold text-green-400 uppercase tracking-widest text-center">
              {t.rematchQuestion}
            </h2>
            <p className="text-[8px] lg:text-xs text-gray-400 text-center uppercase leading-relaxed">
              {t.rematchWants} <span className="text-green-400 font-bold">{t.rematchExclaim}</span>
            </p>
            <div className="flex gap-3 w-full">
              <button
                onClick={onDeclineRematch}
                className="flex-1 px-3 py-2 lg:px-6 lg:py-3 pixel-border bg-black/60 text-[8px] lg:text-xs text-gray-400 font-bold uppercase hover:bg-red-900/40 hover:text-red-400 transition-all"
              >
                {t.decline}
              </button>
              <button
                onClick={onAcceptRematch}
                className="flex-1 px-3 py-2 lg:px-6 lg:py-3 pixel-border bg-green-600 text-[8px] lg:text-xs text-black font-bold uppercase hover:bg-green-500 transition-all"
              >
                {t.accept}
              </button>
            </div>
          </div>
        </div>
      )}

      <header className="w-full relative shrink-0 pt-2 lg:pt-4 text-center">
        <button
          onClick={handleLeaveClick}
          className="absolute top-2 lg:top-4 left-2 lg:left-4 text-[8px] lg:text-xs text-gray-500 hover:text-red-400 transition-colors z-10"
        >
          ← {t.leave}
        </button>

        <div className="flex flex-col items-center justify-center w-full">
          {/* Multiplayer connection indicator */}
          {isMultiplayer && (
            <div className="flex items-center gap-2 mb-1">
              <span
                className={`w-2 h-2 rounded-full ${isConnected ? 'bg-green-500 animate-pulse' : 'bg-red-500'
                  }`}
              ></span>
              <span className="text-[8px] lg:text-xs text-gray-500 uppercase">
                {isConnected ? t.online : t.connectingDots}
              </span>
            </div>
          )}

          <div className="text-[8px] lg:text-sm text-theme mb-0.5 font-bold uppercase tracking-widest">
            {isMultiplayer ? t.multiplayer : t.levelLabel(difficulty)}
          </div>
          <h1 className="text-lg lg:text-4xl font-bold tracking-tighter text-theme drop-shadow-lg uppercase">
            {t.dicepit}
          </h1>
        </div>

        <div className="h-6 lg:h-10 flex items-center justify-center">
          <p
            key={isGameOver ? `end-${message}` : 'live'}
            className={`text-[9px] lg:text-lg tracking-widest uppercase text-center ${isGameOver
              ? ceremony === 'win'
                ? 'msg-punch text-green-400 lg:text-2xl font-bold'
                : ceremony === 'lose'
                  ? 'msg-punch text-red-400 lg:text-2xl font-bold'
                  : 'msg-punch text-yellow-500 lg:text-2xl font-bold'
              : 'text-yellow-500 animate-pulse'
              }`}
          >
            {message}
          </p>
        </div>
      </header>

      <main className="flex-1 w-full flex flex-col lg:flex-row items-center justify-around lg:justify-center gap-2 lg:gap-16 py-2 overflow-hidden">
        {/* Player Grid */}
        <div className="flex flex-col items-center shrink-0 order-3 lg:order-1" style={playerBoardStyle}>
          <div className="flex gap-2 lg:gap-8">
            {playerGrid.map((col, i) => (
              <Column
                key={i}
                col={col}
                index={i}
                canPlace={isPlayerPlacing && (currentDie === 'shield' || col.filter((v) => v !== null).length < 3)}
                shieldRounds={shields ? (playerNumber === 1 ? shields.player1[i] : shields.player2[i]) : 0}
                onClick={() => {
                  if (currentDie === 'shield' && onSelectShieldColumn) {
                    onSelectShieldColumn(i);
                  } else {
                    onPlaceDie(i);
                  }
                }}
                lastAction={lastAction}
                isPlayer={true}
                fx={boardFx.player}
                tallyDelay={ceremony ? i * 0.15 : null}
              />
            ))}
          </div>
          <div className="mt-1 lg:mt-4 text-[8px] lg:text-xl font-bold text-blue-400 tracking-widest uppercase flex items-center gap-2">
            {isMultiplayer && isWaiting && amReady && <span className="text-green-400">✓</span>}
            {profile.name ? profile.name : <span className="animate-pulse text-gray-500">{t.loading}</span>}{' '}
            <span className="text-[8px] lg:text-xs bg-theme text-white px-1 lg:px-2 py-0.5 pixel-border border-2">
              {t.lvl} {profile.level}
            </span>{' '}
            • <AnimatedNumber value={playerTotal} flash />
          </div>
        </div>

        {/* Center: Die / Ready Button / Roll Button */}
        <div className="flex flex-row lg:flex-col items-center justify-center gap-4 lg:gap-10 shrink-0 order-2 lg:order-2">
          <div id="center-die" className="pixel-card p-2 lg:p-8 flex flex-col items-center justify-center min-h-[70px] lg:min-h-[220px] min-w-[70px] lg:min-w-[220px] shadow-2xl relative">
            {/* Turn timer ring (during placing phase when timer is active) */}
            {turnTimer !== null && turnTimer !== undefined && (gameStatus === 'placing' || gameStatus === 'PLAYER_PLACING') && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10">
                {/* SVG countdown ring */}
                <svg
                  className="absolute w-[90px] h-[90px] lg:w-[200px] lg:h-[200px] -rotate-90"
                  viewBox="0 0 100 100"
                >
                  {/* Background ring */}
                  <circle
                    cx="50" cy="50" r="46"
                    fill="none"
                    stroke="rgba(255,255,255,0.08)"
                    strokeWidth="4"
                  />
                  {/* Countdown ring */}
                  <circle
                    cx="50" cy="50" r="46"
                    fill="none"
                    stroke={turnTimer <= 1 ? '#ef4444' : turnTimer <= Math.ceil(turnTimerMax / 3) ? '#f59e0b' : '#22c55e'}
                    strokeWidth="4"
                    strokeLinecap="round"
                    strokeDasharray={`${2 * Math.PI * 46}`}
                    strokeDashoffset={`${2 * Math.PI * 46 * (1 - turnTimer / turnTimerMax)}`}
                    style={{ transition: 'stroke-dashoffset 0.3s ease-out, stroke 0.3s ease-out' }}
                  />
                </svg>
                {/* Timer number - top right corner */}
                <span
                  className={`absolute -top-1 -right-1 lg:top-0 lg:right-0 w-6 h-6 lg:w-10 lg:h-10 rounded-full flex items-center justify-center font-bold text-[10px] lg:text-lg pixel-border shadow-lg ${turnTimer <= 1
                    ? 'bg-red-600 text-white animate-pulse'
                    : turnTimer <= Math.ceil(turnTimerMax / 3)
                      ? 'bg-yellow-500 text-black'
                      : 'bg-green-600 text-black'
                    }`}
                >
                  {turnTimer}
                </span>
              </div>
            )}

            {/* Multiplayer waiting state - show READY toggle button */}
            {isMultiplayer && isWaiting && onSetReady ? (
              <>
                <div className="w-16 h-16 lg:w-32 lg:h-32 border-2 lg:border-4 border-dashed border-gray-700 rounded-lg flex items-center justify-center text-[8px] lg:text-xs text-gray-500 text-center p-1 uppercase mb-2 overflow-hidden">
                  {!opponentJoined ? (
                    <span className="animate-pulse leading-tight">{t.waitingForOpponentDots}</span>
                  ) : amReady && !opponentReady ? (
                    <span className="animate-pulse leading-tight">{t.waitingForOpponentDots}</span>
                  ) : (
                    <span className="leading-tight">{t.pressReady}</span>
                  )}
                </div>
                {opponentJoined && (
                  <button
                    onClick={onSetReady}
                    className={`px-4 lg:px-8 py-2 lg:py-4 pixel-border text-[9px] lg:text-sm font-bold active:translate-y-1 transition-all uppercase whitespace-nowrap ${amReady
                      ? 'bg-red-600 text-white hover:bg-red-500'
                      : 'bg-green-600 text-black hover:bg-green-500'
                      }`}
                  >
                    {amReady ? t.unready : t.ready}
                  </button>
                )}
              </>
            ) : isDieRolling ? (
              /* Rolling animation (both single & multiplayer) */
              <Die value={null} isRolling={true} size="lg" />
            ) : currentDie && (typeof currentDie === 'number' || typeof currentDie === 'string') && currentDie !== 0 ? (
              /* Die has been rolled - show the value */
              <Die value={currentDie} size="lg" />
            ) : isMultiplayer && gameStatus === 'rolling' && !isMyTurn ? (
              /* Opponent's turn to roll - show their rolling animation */
              <Die value={null} isRolling={true} size="lg" />
            ) : isMultiplayer && gameStatus === 'placing' && !isMyTurn ? (
              /* Opponent is placing - show their die value */
              currentDie && currentDie !== 0 ? (
                <Die value={currentDie} size="lg" />
              ) : (
                <div className="w-16 h-16 lg:w-32 lg:h-32 border-2 lg:border-4 border-dashed border-yellow-700/50 rounded-lg flex items-center justify-center text-[8px] lg:text-xs text-yellow-500 text-center p-1 uppercase animate-pulse overflow-hidden">
                  <span className="leading-tight">{t.opponentPlacingShort}</span>
                </div>
              )
            ) : (
              <div className="w-16 h-16 lg:w-32 lg:h-32 border-2 lg:border-4 border-dashed border-gray-700 rounded-lg flex items-center justify-center text-[8px] lg:text-xs text-gray-500 text-center p-1 uppercase overflow-hidden">
                <span className="leading-tight">{t.wait}</span>
              </div>
            )}
          </div>

          <div className="flex flex-col gap-2 lg:w-full">
            {!(isMultiplayer && isWaiting) && (
              <div className="flex items-center justify-center gap-3 lg:gap-5 py-1">
                <AnimatedNumber
                  key={ceremony ? 'tally-p' : 'live-p'}
                  value={playerTotal}
                  countFrom={ceremony ? 0 : undefined}
                  duration={ceremony ? 1100 : undefined}
                  className="text-xs lg:text-2xl font-bold text-blue-400"
                  flash
                />
                <span className="text-[8px] lg:text-xs text-gray-400 font-bold">{t.vs}</span>
                <AnimatedNumber
                  key={ceremony ? 'tally-o' : 'live-o'}
                  value={opponentTotal}
                  countFrom={ceremony ? 0 : undefined}
                  duration={ceremony ? 1100 : undefined}
                  className="text-xs lg:text-2xl font-bold text-red-400"
                  flash
                />
              </div>
            )}

            {expChange !== null && (gameStatus === 'GAME_OVER' || gameStatus === 'game_over') && (
              <div className="flex flex-col items-center gap-0.5">
                <div
                  className={`text-center font-bold text-[8px] lg:text-sm animate-bounce ${expChange > 0 ? 'text-green-400' : 'text-red-400'
                    }`}
                >
                  {expChange > 0 ? `+${expChange}` : expChange} {t.exp}
                </div>
                <div className="text-[8px] lg:text-xs text-gray-500 font-mono">
                  {t.lvl} {profile.level} • {profile.exp} {t.exp}
                </div>
              </div>
            )}

            {/* ── Guest sign-up nudge ─────────────────────────────── */}
            {isGuest && onSignUp && (gameStatus === 'GAME_OVER' || gameStatus === 'game_over') && (
              <div className="flex flex-col items-center gap-1.5 py-1 max-w-[180px] lg:max-w-[250px] mx-auto">
                <div className="text-[8px] lg:text-[10px] text-gray-400 uppercase tracking-wider text-center leading-relaxed">
                  {t.guestSaveHint}
                </div>
                <button
                  onClick={onSignUp}
                  className="px-3 py-2 lg:px-6 lg:py-3 pixel-border bg-yellow-900/40 text-yellow-400 hover:bg-yellow-800/60 text-[8px] lg:text-xs font-bold uppercase tracking-wider active:translate-y-1 transition-all whitespace-nowrap"
                >
                  {t.guestSaveCta}
                </button>
              </div>
            )}

            {(gameStatus === 'GAME_OVER' || gameStatus === 'game_over') && (
              <>
                {/* ── Rematch flow (multiplayer only) ─────────────────── */}
                {isMultiplayer && onRequestRematch && rematchState === 'none' && players.length >= 2 && (
                  <button
                    onClick={onRequestRematch}
                    className="px-4 lg:px-8 py-3 lg:py-6 bg-green-600 text-black pixel-border text-[9px] lg:text-sm font-bold active:translate-y-1 transition-all uppercase whitespace-nowrap"
                  >
                    {t.rematch}
                  </button>
                )}

                {isMultiplayer && rematchState === 'sent' && (
                  <button
                    disabled
                    className="px-4 lg:px-8 py-3 lg:py-6 bg-gray-700 text-gray-400 pixel-border text-[9px] lg:text-sm font-bold uppercase whitespace-nowrap cursor-not-allowed animate-pulse"
                  >
                    {t.waiting}
                  </button>
                )}

                {isMultiplayer && rematchState === 'declined' && (
                  <div className="text-[9px] lg:text-sm text-red-400 font-bold uppercase tracking-widest animate-pulse">
                    {t.rematchDeclined}
                  </div>
                )}

                <button
                  onClick={onReset}
                  className="px-4 lg:px-8 py-3 lg:py-6 bg-yellow-600 text-black pixel-border text-[9px] lg:text-sm font-bold active:translate-y-1 transition-all uppercase whitespace-nowrap"
                >
                  {t.leave}
                </button>
              </>
            )}
          </div>
        </div>

        {/* Opponent Grid */}
        <div className="flex flex-col items-center shrink-0 order-1 lg:order-3" style={opponentBoardStyle}>
          <div className="mb-1 lg:mb-4 text-[8px] lg:text-xl font-bold text-red-400 tracking-widest uppercase flex items-center gap-2">
            {isMultiplayer && isWaiting && opponentReady && <span className="text-green-400">✓</span>}
            {isMultiplayer && players.length > 1
              ? opponentPlayer?.name || t.opponent
              : t.pitLord}{' '}
            {isMultiplayer && players.length > 1 && (
              <span className="text-[8px] lg:text-xs bg-red-900/60 text-red-300 px-1 lg:px-2 py-0.5 pixel-border border-2 border-red-700/50">
                {t.lvl} {opponentPlayer?.level ?? '?'}
              </span>
            )}
            • <AnimatedNumber value={opponentTotal} flash />
          </div>
          <div className="flex gap-2 lg:gap-8">
            {opponentGrid.map((col, i) => (
              <Column
                key={i}
                col={col}
                index={i}
                canPlace={false}
                shieldRounds={shields ? (playerNumber === 1 ? shields.player2[i] : shields.player1[i]) : 0}
                onClick={() => { }}
                lastAction={lastAction}
                isPlayer={false}
                fx={boardFx.opponent}
                tallyDelay={ceremony ? i * 0.15 : null}
              />
            ))}
          </div>
        </div>
      </main>

      {/* Countdown Overlay */}
      {countdownNumber !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
          <div className="text-center animate-pulse">
            <div
              className="text-[120px] lg:text-[200px] font-bold text-theme animate-bounce"
              style={{ filter: 'drop-shadow(0 0 30px var(--theme-glow))' }}
            >
              {countdownNumber === 0 ? t.go : countdownNumber}
            </div>
            <div className="text-2xl lg:text-4xl font-bold text-white uppercase tracking-widest mt-4 animate-pulse">
              {countdownNumber === 0 ? t.go : t.getReady}
            </div>
          </div>
        </div>
      )}

      <footer className="w-full text-center pb-2 lg:pb-6 shrink-0">
        {isMultiplayer && roomId && (
          <div className="mb-3 flex flex-col items-center gap-2">
            <div className="flex items-center gap-2 bg-black/30 px-3 py-1.5 rounded pixel-border">
              <span className="text-[8px] lg:text-xs text-gray-400 uppercase tracking-wide">{t.room}</span>
              <button
                onClick={copyRoomUrl}
                className="px-2 py-0.5 bg-theme/20 border border-theme/50 text-theme text-[8px] lg:text-xs font-mono font-bold uppercase tracking-wider hover:bg-theme/40 hover:text-white transition-all"
              >
                {roomId.slice(0, 8).toUpperCase()}
              </button>
            </div>
          </div>
        )}
        <p className="text-[8px] lg:text-xs text-gray-400 font-mono opacity-60 tracking-tighter uppercase">
          {isMultiplayer ? t.multiplayerBattle : t.matchDice}
        </p>
      </footer>

      {/* Chat (multiplayer only) */}
      {isMultiplayer && onSendChat && (
        <ChatBox
          messages={chatMessages}
          onSend={onSendChat}
          playerName={profile.name}
        />
      )}
    </div>
  );
};
